"use client";
import { Modal } from "@cmp-src/overlay/modals/Modal";
import { Button } from "@cmp-src/primitives/button";
import type { ISchedule } from "@gettactic/api";
import { getCurrentUserCanCancel } from "@gettactic/helpers/src/resources/getCurrentUserCanCancel";
import { useAuthenticated } from "@gettactic/hooks";
import { format, parseISO } from "date-fns";
import React from "react";
import { CancelReservationsDay } from "@/features/reserve/components/CancelReservations/CancelReservationsDay";
import { groupReservations } from "@/features/reserve/components/CancelReservations/groupReservations";
import { ReleaseDeskOption } from "@/features/reserve/components/CancelReservations/ReleaseDeskOption";
import { ReservationCancel } from "@/features/reserve/components/ReservationCancel/ReservationCancel";
import {
  isReleaseDeskSelectionComplete,
  KEEP_DESK_SELECTION,
  RELEASE_TO_ANYONE_SELECTION,
  type ReleaseDeskSelection
} from "@/features/reserve/hooks/useReleaseDedicatedDesk";
import { useCustomParkingVerbiageOnWeb } from "@/lib/hooks/useCustomParkingVerbiage";
import type { PendingLocationChange } from "../hooks/useDashboardLocation";
import { describeWorkingLocation } from "../lib/reservationsElsewhere";

export interface LocationChangeReservationsModalProps {
  /** The working location change waiting on the person's answer. */
  pending: PendingLocationChange | null;
  /** Closing without answering leaves the working location as it was. */
  onClose: () => void;
}

/**
 * Asked before a working location change lands on a day with reservations
 * somewhere else: the desk kept in the old office, everything booked for a
 * day that is now remote. Lists them the way the cancel-reservations tool
 * does, so the person cancels the ones they will not use and keeps the rest,
 * and the day's location changes either way. Someone leaving the office of
 * their dedicated desk is also offered to release it for the day.
 */
export const LocationChangeReservationsModal: React.FC<
  LocationChangeReservationsModalProps
> = ({ pending, onClose }) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const parkingVerbiage = useCustomParkingVerbiageOnWeb();
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  // Everything the cancel confirmation needs, taken together the moment it
  // opens: which change it belongs to, which reservations it is cancelling,
  // and what was chosen for the desk. One piece of state and not three,
  // because the confirmation outlives the prompt that opened it - a second
  // day's reservations can land while it is up - and three would come apart
  // one at a time, leaving the confirmation acting on some of this day and
  // some of the next.
  const [confirmation, setConfirmation] = React.useState<{
    pending: PendingLocationChange;
    schedules: ISchedule[];
    selection: ReleaseDeskSelection;
  } | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = React.useState(false);
  // The day whose answer is being carried out, if any. The disabled flags
  // follow a state update, so two quick clicks can both get in before React
  // has re-rendered and the day would be changed twice.
  //
  // By day rather than a plain flag: a prompt for one day can be replaced by
  // another day's while the first answer is still being written, and a flag
  // set by the first would swallow the click on the second.
  const [proceedingDate, setProceedingDate] = React.useState<string | null>(
    null
  );
  const running = React.useRef<Set<string>>(new Set());

  // Releasing the dedicated desk for the day, when the change leaves its
  // office. It starts on "anyone": the desk would sit empty otherwise.
  const desk = pending?.desk ?? null;
  const [releaseSelection, setReleaseSelection] =
    React.useState<ReleaseDeskSelection>(RELEASE_TO_ANYONE_SELECTION);

  const reservations = React.useMemo(
    () => pending?.reservations ?? [],
    [pending]
  );
  const lockedIds = React.useMemo(() => {
    const locked = new Set<string>();
    for (const schedule of reservations) {
      if (!getCurrentUserCanCancel(schedule, authenticatedUser)) {
        locked.add(schedule.id);
      }
    }
    return locked;
  }, [reservations, authenticatedUser]);
  const selectableSchedules = React.useMemo(
    () => reservations.filter((schedule) => !lockedIds.has(schedule.id)),
    [reservations, lockedIds]
  );

  // Every cancellable reservation starts selected: the point of the prompt is
  // that these are in the way. The desk starts on "anyone" only when it is the
  // whole prompt; with reservations to answer for, releasing the desk is the
  // deliberate extra rather than the default.
  const selectableRef = React.useRef(selectableSchedules);
  selectableRef.current = selectableSchedules;
  const hasReservationsToAnswer =
    (pending?.reservations.length ?? 0) > 0 || !!pending?.isTruncated;

  // Keyed on the change being asked about, not on the derived list: that list
  // is rebuilt whenever the authenticated user object changes identity, which
  // happens on any background refetch, and re-running this would throw away
  // what the person had picked.
  const pendingKey = pending
    ? `${pending.date}:${pending.newLocationId}:${pending.desk?.id ?? ""}`
    : null;
  React.useEffect(() => {
    setSelectedIds(
      new Set(selectableRef.current.map((schedule) => schedule.id))
    );
    // The confirmation is left alone entirely - not even closed. It asks
    // about the day it was opened for and now carries that day with it, so
    // it is still a good question; and closing it would let the prompt
    // underneath be dismissed, which unmounts the confirmation with its
    // deletes possibly still running. ConfirmModal refuses to close itself
    // while they are, so nothing else can take it off the screen mid-flight.
    setReleaseSelection(
      hasReservationsToAnswer
        ? KEEP_DESK_SELECTION
        : RELEASE_TO_ANYONE_SELECTION
    );
  }, [pendingKey, hasReservationsToAnswer]);

  const groups = React.useMemo(
    () => groupReservations(reservations),
    [reservations]
  );
  const selectedSchedules = selectableSchedules.filter((schedule) =>
    selectedIds.has(schedule.id)
  );

  const toggleOne = React.useCallback((schedule: ISchedule) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(schedule.id)) {
        next.delete(schedule.id);
      } else {
        next.add(schedule.id);
      }
      return next;
    });
  }, []);

  const toggleMany = React.useCallback(
    (targets: ISchedule[], selected: boolean) => {
      setSelectedIds((current) => {
        const next = new Set(current);
        for (const schedule of targets) {
          if (selected) {
            next.add(schedule.id);
          } else {
            next.delete(schedule.id);
          }
        }
        return next;
      });
    },
    []
  );

  // Both the change to carry out and the release chosen for it are handed in
  // rather than read live, because neither is still guaranteed to be what it
  // was when the button was pressed. The prompt stays interactive behind the
  // cancel confirmation, so the desk can be toggled in between; and another
  // day's reservations can land while that confirmation is up, putting a
  // different change on screen entirely. Reading either from the outside here
  // would change a day nobody was asked about.
  //
  // The change itself carries out the release: the day has to stay held for
  // both, and only the hook that holds it can do that.
  const proceed = React.useCallback(
    async (target: PendingLocationChange, selection: ReleaseDeskSelection) => {
      if (running.current.has(target.date)) return;
      const date = target.date;
      running.current.add(date);
      setProceedingDate(date);
      try {
        await target.proceed(selection);
      } finally {
        running.current.delete(date);
        setProceedingDate((current) => (current === date ? null : current));
      }
    },
    []
  );

  // Not while the cancel confirmation is up: that question belongs to this
  // change, and dismissing the prompt underneath would unmount it.
  const closePrompt = () => {
    if (isConfirmOpen) {
      return;
    }
    onClose();
  };

  if (!pending) {
    return null;
  }

  const day = format(parseISO(`${pending.date}T00:00:00`), "EEEE, MMM d");
  const where = describeWorkingLocation(
    pending.newLocationId,
    authenticatedUser.offices?.offices ?? []
  );
  const count = selectedSchedules.length;
  const hasReservations = reservations.length > 0 || pending.isTruncated;
  // This prompt's own day: an answer still being written for a day this
  // prompt replaced must not disable the buttons of the one on screen.
  const isProceeding = proceedingDate === pending.date;
  // Nothing proceeds while a release to specific people names nobody yet.
  const isBusy =
    isProceeding || !isReleaseDeskSelectionComplete(releaseSelection);

  return (
    <>
      <Modal
        isOpen
        onClose={closePrompt}
        title={
          hasReservations ? "Reservations somewhere else" : "Free your desk?"
        }
        description={
          hasReservations
            ? `On ${day} you'll be ${where}. These reservations are not where you'll be — cancel the ones you won't use, or keep them.`
            : `On ${day} you'll be ${where}. Your desk stays yours unless you release it for the day.`
        }
        size="lg"
      >
        <Modal.Body>
          <div className="flex flex-col gap-3">
            {desk && (
              <ReleaseDeskOption
                deskName={desk.name}
                selection={releaseSelection}
                onSelectionChange={setReleaseSelection}
                dayLabel="that day"
              />
            )}
            {pending.isTruncated && (
              <p className="text-destructive text-sm">
                You have more reservations that day than can be listed here.
                Review the rest in your agenda.
              </p>
            )}
            <div className="max-h-[24rem] overflow-y-auto">
              {groups.map((group) => (
                <CancelReservationsDay
                  key={group.date}
                  group={group}
                  selectedIds={selectedIds}
                  lockedIds={lockedIds}
                  onToggle={toggleOne}
                  onToggleMany={toggleMany}
                  parkingVerbiage={parkingVerbiage}
                />
              ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          {hasReservations ? (
            <>
              <Button
                type="button"
                variant="outline"
                disabled={isBusy}
                onClick={() => void proceed(pending, releaseSelection)}
              >
                {releaseSelection.choice === "keep"
                  ? "Keep reservations"
                  : "Keep reservations, release desk"}
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={count === 0 || isBusy}
                onClick={() => {
                  // Snapshot the selection: nothing between this click and
                  // the confirmation should change what gets cancelled, nor
                  // what happens to the desk.
                  setConfirmation({
                    pending,
                    schedules: selectedSchedules,
                    selection: releaseSelection
                  });
                  setIsConfirmOpen(true);
                }}
              >
                {/* Cancelling runs proceed() too, which releases the desk
                    when one was chosen, so the label has to say so. */}
                {count > 0
                  ? `Cancel ${count} reservation${count === 1 ? "" : "s"}${
                      releaseSelection.choice === "keep" ? "" : ", release desk"
                    }`
                  : "Cancel reservations"}
              </Button>
            </>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                disabled={isProceeding || isConfirmOpen}
                onClick={closePrompt}
              >
                Don't change
              </Button>
              <Button
                type="button"
                disabled={isBusy}
                onClick={() => void proceed(pending, releaseSelection)}
              >
                {releaseSelection.choice === "keep"
                  ? "Continue"
                  : "Release desk and continue"}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>

      <ReservationCancel
        open={isConfirmOpen}
        setOpen={setIsConfirmOpen}
        schedules={confirmation?.schedules ?? []}
        // The location changes once the reservations are gone - the one the
        // confirmation was opened for, not whatever prompt is up by now.
        onCancelSuccess={() => {
          if (confirmation) {
            void proceed(confirmation.pending, confirmation.selection);
          }
        }}
      />
    </>
  );
};
